import { io } from 'socket.io-client';
import config from '../config';

class WebSocketManager {
    constructor() {
        console.log('[WebSocketManager] Initializing with config:', {
            WS_BASE_URL: config.WS_BASE_URL,
            API_BASE_URL: config.API_BASE_URL
        });
        this.socket = null;
        this.listeners = new Map();
        this.connected = false;
        this.room = null;
        // Set of rooms to (re)join on connect. Why a Set, not a single slot:
        // TosDashboard calls joinRoom twice in quick succession (event_X then
        // hotel_Y) before the socket finishes its handshake. A single-slot
        // pendingRoom would drop the first call, leaving the event room
        // un-joined and TOS blind to event-only broadcasts. Keeping a Set also
        // means a transient disconnect doesn't lose membership — every room
        // is re-joined on every 'connect' event.
        this.pendingRooms = new Set();
        // Refcount per room. Multiple components (e.g. TosDashboard and the
        // embedded EmployeeManagement) often want to be in the SAME room. Without
        // refcounting, the first component to clean up would silently kick the
        // still-active parent out. joinRoom increments; leaveRoom decrements and
        // only emits leave_room when the last subscriber drops to 0.
        this.roomRefCounts = new Map();
    }

    connect() {
        // Reuse an existing socket instance. socket.io owns reconnection: it keeps
        // the SAME instance alive across drops and retries in the background. Creating
        // a second io() here (which the old `if (this.socket) this.disconnect()` path
        // did whenever this.socket had been nulled) left orphaned sockets and dead
        // listeners, and every dashboard stopped receiving events.
        if (this.socket) {
            if (this.socket.connected) {
                console.log('[WebSocketManager] Reusing connected socket');
                // Still (re-)assert room membership: callers often call connect()
                // precisely because they suspect they fell out of their rooms.
                this.connected = true;
                this.rejoinPendingRooms();
                return;
            }
            if (this.socket.active || this.socket.connecting) {
                // Handshake / reconnection already in flight — let it finish.
                console.log('[WebSocketManager] Socket already connecting, waiting');
                return;
            }
            // Instance exists but is fully closed (explicit disconnect earlier):
            // ask it to reconnect rather than building a new one.
            console.log('[WebSocketManager] Re-opening existing socket');
            try {
                this.socket.connect();
                return;
            } catch (e) {
                console.warn('[WebSocketManager] socket.connect() failed, recreating:', e && e.message);
                this.socket = null;
            }
        }

        try {
            const socket = io(config.WS_BASE_URL, {
                // WebSocket only. This is what already happened in practice
                // (engine.io-client 6 does not fall back to polling unless
                // tryAllTransports is set), and long-polling cannot work with
                // several server workers and no sticky sessions: follow-up polls
                // land on a worker that doesn't know the session (HTTP 400).
                transports: ['websocket'],
                path: '/socket.io',
                secure: true,
                rejectUnauthorized: false,
                timeout: 10000,
                // Production gunicorn workers recycle every 15-25 min at peak, so
                // disconnects are routine and MUST be retried forever — a finite
                // reconnectionAttempts meant the app went permanently deaf until a
                // force-close.
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 10000,
                randomizationFactor: 0.5
            });
            this.socket = socket;

            console.log('[WebSocketManager] Socket instance created');

            // NOTE: every handler below closes over `socket`, never `this.socket`.
            // `this.socket` can be reassigned/nulled between a disconnect and the
            // reconnect that follows; the closure always refers to the instance
            // that actually fired the event.
            socket.on('connect', () => {
                console.log('[WebSocketManager] WebSocket connection established', {
                    id: socket.id || null
                });
                this.connected = true;
                this.rejoinPendingRooms(socket);
                // Socket.IO does not replay events missed while disconnected
                // (network blip, server worker recycle). Tell listeners on every
                // re-connect after the first so screens can catch up; see
                // useResumeSync, which refreshes on this like an app resume.
                if (this.hasConnectedBefore) this.handleEvent('reconnected', {});
                this.hasConnectedBefore = true;
            });

            socket.on('disconnect', (reason) => {
                console.log('[WebSocketManager] WebSocket connection closed', { reason });
                this.connected = false;
                // Deliberately NOT `this.socket = null`. socket.io keeps this very
                // instance and reconnects with it; nulling it made the next
                // `this.socket.emit(...)` throw "Cannot read properties of null".
                // Only the explicit disconnect() method clears the reference.
            });

            socket.on('connect_error', (error) => {
                console.error('[WebSocketManager] WebSocket connection error:', error && error.message);
            });

            socket.on('error', (error) => {
                console.error('[WebSocketManager] WebSocket error:', error && error.message);
            });

            // Server-event fan-out. Payloads are intentionally NOT logged here —
            // handleEvent logs the event name and listener count once.
            const SERVER_EVENTS = [
                'booking_update',
                'booking_status_change',
                'user_status_change',
                'vsp_notification',
                'parking_slot_update',
                'new_employee_submission',
                'employee_verification_update',
                'employee_removed',
                // Emitted by the server but previously missing here, so the
                // event-organiser / VSP / TOS listeners for them never ran.
                'event_changed',
                'event_removed',
                'vsp_request_changed'
            ];
            for (const eventName of SERVER_EVENTS) {
                socket.on(eventName, (data) => {
                    this.handleEvent(eventName, data);
                });
            }

        } catch (error) {
            console.error('[WebSocketManager] Error creating socket connection:', {
                error: error.message,
                stack: error.stack
            });
        }
    }

    // Re-emit join_room for every tracked room. Called on every 'connect' so a
    // reconnect restores membership, and from connect() when the socket is already up.
    rejoinPendingRooms(socketInstance) {
        const s = socketInstance || this.socket;
        if (!s || this.pendingRooms.size === 0) return;
        console.log('[WebSocketManager] Joining rooms:', Array.from(this.pendingRooms));
        for (const room of this.pendingRooms) {
            try {
                s.emit('join_room', { room });
            } catch (e) {
                console.error('[WebSocketManager] Failed to join room', room, e && e.message);
            }
        }
    }

    handleEvent(eventName, data) {
        const eventListeners = this.listeners.get(eventName) || [];
        console.log('[WebSocketManager] Event:', eventName, 'listeners:', eventListeners.length);
        eventListeners.forEach((listener, index) => {
            try {
                listener(data);
            } catch (error) {
                console.error('[WebSocketManager] Error in event listener:', {
                    eventName,
                    listenerIndex: index,
                    error: error.message
                });
            }
        });
    }

    disconnect() {
        console.log('[WebSocketManager] Disconnecting WebSocket...', {
            wasConnected: this.connected,
            hasSocket: !!this.socket
        });
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            this.connected = false;
        }
    }

    joinRoom(room) {
        const prev = this.roomRefCounts.get(room) || 0;
        const next = prev + 1;
        this.roomRefCounts.set(room, next);
        console.log('[WebSocketManager] Joining room:', {
            room,
            refCount: next,
            connected: this.connected,
            hasSocket: !!this.socket
        });
        this.room = room;
        // Track every requested room so reconnects re-join all of them.
        this.pendingRooms.add(room);
        // Emit join_room only on the first joiner — subsequent joins for the
        // same room are no-ops on the server (we're already in it).
        if (this.connected && this.socket && prev === 0) {
            console.log('[WebSocketManager] Emitting join_room event');
            this.socket.emit('join_room', { room });
        } else if (!this.connected || !this.socket) {
            console.log('[WebSocketManager] Connection not ready, storing room for later');
        }
    }

    leaveRoom(room) {
        const cur = this.roomRefCounts.get(room) || 0;
        if (cur <= 1) {
            // Last subscriber leaving — actually exit the room on the server.
            this.roomRefCounts.delete(room);
            this.pendingRooms.delete(room);
            console.log('[WebSocketManager] Leaving room (last subscriber):', {
                room,
                connected: this.connected,
                hasSocket: !!this.socket
            });
            if (this.connected && this.socket) {
                this.socket.emit('leave_room', { room });
            }
            if (this.room === room) this.room = null;
        } else {
            this.roomRefCounts.set(room, cur - 1);
            console.log('[WebSocketManager] Decrementing room refCount (still has subscribers):', {
                room,
                refCount: cur - 1
            });
        }
    }

    emit(event, data) {
        console.log('[WebSocketManager] Emitting event:', {
            event,
            data,
            connected: this.connected,
            hasSocket: !!this.socket
        });
        
        if (!this.connected || !this.socket) {
            console.warn('[WebSocketManager] Cannot emit event - not connected');
            return;
        }

        try {
            this.socket.emit(event, data);
        } catch (error) {
            console.error('[WebSocketManager] Error emitting event:', {
                event,
                error: error.message
            });
        }
    }

    addListener(event, callback) {
        console.log('[WebSocketManager] Adding listener for event:', {
            event,
            currentListenerCount: this.listeners.get(event)?.length || 0
        });
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }

    removeListener(event, callback) {
        console.log('[WebSocketManager] Removing listener for event:', {
            event,
            currentListenerCount: this.listeners.get(event)?.length || 0
        });
        if (this.listeners.has(event)) {
            const callbacks = this.listeners.get(event);
            const index = callbacks.indexOf(callback);
            if (index !== -1) {
                callbacks.splice(index, 1);
            }
        }
    }
}

export const websocketManager = new WebSocketManager();

// Rooms a customer's app joins to get updates for their own bookings only.
// Mirrors customer_rooms() in product_server/routes/websocket.py.
export function customerRooms(phone, email) {
    const rooms = [];
    const digits = String(phone || '').replace(/\D/g, '');
    if (digits.length >= 10) rooms.push(`customer_${digits.slice(-10)}`);
    if (email) rooms.push(`customer_email_${String(email).trim().toLowerCase()}`);
    return rooms;
}
